import { BaseModel } from './BaseModel';

export class User extends BaseModel {
    id: number;
    name: string;
    email: string;
    is_admin: boolean;
    is_approved: boolean;
    is_reliable: boolean;
    approved_at: string | null;
    oauth_provider: string | null;
    oauth_id: string | null;
    avatar: string | null;
    avatar_64_url?: string | null;
    avatar_400_url?: string | null;
    created_at?: string;
    updated_at?: string;
    country_id?: number | null;
    country?: {
        id: number;
        english_name: string;
        native_name: string;
        iso2: string;
        iso3: string;
    } | null;
    xp_total?: number;
    current_level?: number;

    constructor(data?: Partial<User>) {
        super();
        this.id = data?.id ?? 0;
        this.name = data?.name ?? '';
        this.email = data?.email ?? '';
        this.is_admin = data?.is_admin ?? false;
        this.is_approved = data?.is_approved ?? true;
        this.is_reliable = data?.is_reliable ?? false;
        this.approved_at = data?.approved_at ?? null;
        this.oauth_provider = data?.oauth_provider ?? null;
        this.oauth_id = data?.oauth_id ?? null;
        this.avatar = data?.avatar ?? null;
        this.avatar_64_url = data?.avatar_64_url ?? null;
        this.avatar_400_url = data?.avatar_400_url ?? null;
        this.created_at = data?.created_at;
        this.updated_at = data?.updated_at;
        this.country_id = data?.country_id ?? null;
        this.country = data?.country ?? null;
        this.xp_total = data?.xp_total;
        this.current_level = data?.current_level;
    }

    static fromJson(json: any): User {
        return new User(json);
    }

    get displayName(): string {
        return this.name || this.email;
    }

    get initials(): string {
        if (!this.name) return this.email.charAt(0).toUpperCase();
        const parts = this.name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        }
        return this.name.charAt(0).toUpperCase();
    }

    // Admin & Approval methods
    isAdmin(): boolean {
        return this.is_admin;
    }

    isApproved(): boolean {
        return this.is_approved;
    }

    isReliable(): boolean {
        return this.is_reliable;
    }

    isOAuthUser(): boolean {
        return this.oauth_provider !== null;
    }

    getAuthMethod(): string {
        return this.oauth_provider || 'Email';
    }

    getRoleName(): string {
        return this.is_admin ? 'Admin' : 'User';
    }

    getStatusName(): string {
        return this.is_approved ? 'Approved' : 'Pending';
    }

    getRoleBadgeType(): 'admin' | 'user' {
        return this.is_admin ? 'admin' : 'user';
    }

    getStatusBadgeType(): 'approved' | 'pending' {
        return this.is_approved ? 'approved' : 'pending';
    }

    getAuthBadgeType(): 'oauth' | 'regular' {
        return this.oauth_provider ? 'oauth' : 'regular';
    }

    getFormattedJoinDate(): string {
        return this.created_at ? new Date(this.created_at).toLocaleDateString() : '';
    }

    hasAvatar(): boolean {
        return this.avatar !== null;
    }

    getAvatarUrl(): string | null {
        return this.avatar;
    }

    getAvatarUrl64(): string | null {
        return this.avatar_64_url ?? this.avatar ?? null;
    }

    getAvatarUrl400(): string | null {
        return this.avatar_400_url ?? this.avatar ?? null;
    }

    canSetPassword(): boolean {
        // OAuth users without password can SET a password
        return this.isOAuthUser();
    }

    getCountryName(): string {
        if (this.country) {
            return this.country.english_name;
        }
        return '';
    }
}

